import { type FieldValues, useController } from 'react-hook-form';
import { CodeEditorField } from '../CodeEditorField/CodeEditorField';
import type { CodeEditorFieldProps } from '../CodeEditorField/CodeEditorField.types';
import type {
  FormControlProps,
  TypedUseControllerProps,
} from '../FormControl/FormControl.types';
import { useStableCallback } from '../hooks/useStableCallback';

export type FormCodeEditorProps<
  TFieldValues extends FieldValues = FieldValues,
> = TypedUseControllerProps<TFieldValues, string> &
  FormControlProps<CodeEditorFieldProps, 'inputRef'>;

export const FormCodeEditor = <TFieldValues extends FieldValues = FieldValues>({
  disabled,
  onBlur,
  ...props
}: FormCodeEditorProps<TFieldValues>) => {
  const {
    field: {
      ref,
      value,
      onChange,
      onBlur: onFieldBlur,
      disabled: fieldDisabled,
      ...field
    },
    fieldState: { error: fieldError },
  } = useController(props);

  const error = fieldError?.message?.toString();
  const handleBlur = useStableCallback(() => {
    onFieldBlur();
    onBlur?.();
  });

  return (
    <CodeEditorField
      {...props}
      {...field}
      value={value ?? ''}
      onChange={onChange}
      onBlur={handleBlur}
      disabled={disabled ?? fieldDisabled}
      error={error}
      inputRef={ref}
    />
  );
};
